/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Issue } from '@shared/types/issues';
import { UserSettingsData, IntegrationSettings } from '@shared/types/user_settings';
import { ProductAppSettings, WalkthroughState, ProductVelocitySettings, ProductIssuesPageFilters } from '@shared/types/product-app-settings';
import type { ProductsResponseData, ProductResponseData } from '@shared/types/responses/products';
import type { ProductMetadata, ProductImportResult, ProductSyncResult } from '@shared/types/products';
import type { AcceptanceCriterion } from '@shared/types/user-stories';

// Import all submodule functions
import * as settings from './skillshop/settings';
import * as requests from './skillshop/requests';
import * as issues from './skillshop/issues';
import * as userStories from './skillshop/user-stories';
import * as worktrees from './skillshop/worktrees';
import * as products from './skillshop/products';
import * as productMetadata from './skillshop/product-metadata';
import * as codingAgents from './skillshop/coding-agents';
import * as codeIndex from './skillshop/code-index';
import * as auth from './skillshop/auth';
import * as mcp from './skillshop/mcp';
import * as jira from './skillshop/jira';
import * as linear from './skillshop/linear';
import * as vectorSearch from './skillshop/vector-search';
import * as tools from './skillshop/tools';
import * as changelog from './skillshop/changelog';

/**
 * SkillShopService handles API calls for the Velocity product app
 * 
 * Architecture:
 * - SkillShop server calls (port 5173): Use authenticatedFetch() - handles auth, routing
 * - Product server calls (port 3002): Use direct fetch() - local endpoints with repo context
 * 
 * Examples:
 * - authenticatedFetch('/api/llm/prompt') -> SkillShop server (AI tools)
 * - fetch(`${getBaseUrl()}/api/issues/:id`) -> Product server (issue CRUD with repo context)
 */
export class SkillShopService {
  /**
   * Get the base URL for product server endpoints (port 3002)
   * These endpoints handle repo-specific operations
   */
  private getBaseUrl(): string {
    return import.meta.env.DEV ? 'http://localhost:3002' : '';
  }

  constructor() {
    console.log('[SkillShop Service] Initialized');
  }

  //
  // User Settings
  //

  async getUserSettings(): Promise<UserSettingsData | null> {
    return settings.getUserSettings();
  }

  async updateIntegrationSettings(integrationSettings: IntegrationSettings): Promise<UserSettingsData | null> {
    return settings.updateIntegrationSettings(integrationSettings);
  }

  async saveProductAppSettings(
    productSettings: ProductAppSettings,
    baseIntegration: IntegrationSettings | null,
  ): Promise<{ userSettings: UserSettingsData | null; integration: IntegrationSettings | null }> {
    return settings.saveProductAppSettings(productSettings, baseIntegration);
  }

  async updateVelocitySettings(velocitySettings: ProductVelocitySettings): Promise<UserSettingsData | null> {
    return settings.updateVelocitySettings(velocitySettings);
  }

  async updateIssuesPageFilters(filters: ProductIssuesPageFilters): Promise<UserSettingsData | null> {
    return settings.updateIssuesPageFilters(filters);
  }

  async saveWalkthroughState(
    walkthroughState: WalkthroughState,
    baseIntegration: IntegrationSettings | null
  ): Promise<{ success: boolean; error?: string }> {
    return settings.saveWalkthroughState(walkthroughState, baseIntegration);
  }

  async getWalkthroughState(): Promise<WalkthroughState | null> {
    return settings.getWalkthroughState();
  }

  //
  // Request Processing
  //

  async processIssueRequest(request: string, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return requests.processIssueRequest(request, activeProduct);
  }

  async processReleaseRequest(request: string, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return requests.processReleaseRequest(request, activeProduct);
  }

  async processUserStoryRequest(request: string, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return requests.processUserStoryRequest(request, activeProduct);
  }

  async processProductRequest(request: string, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return requests.processProductRequest(request, activeProduct);
  }

  async processHelpQuestion(question: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return requests.processHelpQuestion(question);
  }

  async generateCommitMessage(
    changedFiles: Array<{ path: string; status: string }>,
    diffs?: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    return requests.generateCommitMessage(changedFiles, diffs);
  }

  //
  // Issue Operations
  //

  async groomIssue(issueData: any, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return issues.groomIssue(issueData, activeProduct);
  }

  async archiveCompletedIssues(repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string; archivedCount?: number }> {
    return issues.archiveCompletedIssues(repoUrl, repoPath);
  }

  async updateIssueCodeLog(issueId: string, codeLog: string, productId?: string, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return issues.updateIssueCodeLog(issueId, codeLog, productId, repoUrl, repoPath);
  }

  async updateIssueStatus(issueId: string, status: string, productId?: string, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return issues.updateIssueStatus(issueId, status, productId, repoUrl, repoPath);
  }

  async generateCodeForIssue(issue: Issue, agent: string = 'claude-code', activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return issues.generateCodeForIssue(issue, agent, activeProduct);
  }

  async buildRelease(release: Issue, agent: string = 'claude-code', activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return issues.buildRelease(release, agent, activeProduct);
  }

  //
  // User Story Operations
  //

  async groomUserStory(story: any, activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.groomUserStory(story, activeProduct);
  }

  async groomEpic(epic: any, stories: any[], activeProduct?: any): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.groomEpic(epic, stories, activeProduct);
  }

  async updateUserStoryStatus(storyId: string, newStatus: string, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.updateUserStoryStatus(storyId, newStatus, repoUrl, repoPath);
  }

  async updateUserStoryFields(storyId: string, fields: any, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.updateUserStoryFields(storyId, fields, repoUrl, repoPath);
  }

  async deleteUserStory(storyId: string, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.deleteUserStory(storyId, repoUrl, repoPath);
  }

  async updateEpicFields(epicId: string, fields: any, repoUrl?: string, repoPath?: string): Promise<{ success: boolean; result?: string; error?: string }> {
    return userStories.updateEpicFields(epicId, fields, repoUrl, repoPath);
  }

  //
  // Worktree Operations
  //

  private async createWorktree(repositoryUrl: string, operation: string, baseBranch: string = 'velocity'): Promise<{ success: boolean; worktreePath?: string; error?: string }> {
    return worktrees.createWorktree(repositoryUrl, operation, baseBranch);
  }

  //
  // Product CRUD
  //

  async getProducts(searchQuery?: string): Promise<ProductsResponseData> {
    return products.getProducts(searchQuery);
  }

  async getProductsByOrganization(organizationId: string): Promise<ProductsResponseData> {
    return products.getProductsByOrganization(organizationId);
  }

  async getProduct(id: string): Promise<ProductResponseData | null> {
    return products.getProduct(id);
  }

  async createProduct(product: Record<string, unknown>): Promise<ProductResponseData> {
    return products.createProduct(product);
  }

  async updateProduct(id: string, updates: Record<string, unknown>): Promise<ProductResponseData> {
    return products.updateProduct(id, updates);
  }

  async deleteProduct(id: string): Promise<boolean> {
    return products.deleteProduct(id);
  }

  //
  // Product Metadata (file-based) operations
  //

  async checkProductMetadataExists(repoUrl: string, repoPath?: string | null): Promise<{
    exists: boolean;
    repoCloned: boolean;
    path?: string;
  }> {
    return productMetadata.checkProductMetadataExists(repoUrl, repoPath);
  }

  async readProductMetadata(repoUrl: string, repoPath?: string | null): Promise<ProductMetadata | null> {
    return productMetadata.readProductMetadata(repoUrl, repoPath);
  }

  async writeProductMetadata(
    repoUrl: string,
    product: ProductMetadata,
    repoPath?: string | null,
    commitMessage?: string
  ): Promise<ProductMetadata> {
    return productMetadata.writeProductMetadata(repoUrl, product, repoPath, commitMessage);
  }

  async importProductFromRepo(
    repoUrl: string,
    userOrganizationId: string,
    repoPath?: string | null,
    accessToken?: string,
    useSSH?: boolean,
    autoCreate?: boolean,
    userId?: number
  ): Promise<ProductImportResult> {
    return productMetadata.importProductFromRepo(repoUrl, userOrganizationId, repoPath, accessToken, useSSH, autoCreate, userId);
  }

  async syncProductFromFile(
    productId: string,
    repoUrl: string,
    repoPath?: string | null
  ): Promise<ProductSyncResult> {
    return productMetadata.syncProductFromFile(productId, repoUrl, repoPath);
  }

  async syncProductMetadataOnRepoChange(
    productId: string,
    product: ProductResponseData,
    repoUrl: string,
    repoPath?: string | null
  ): Promise<{
    success: boolean;
    action: 'synced_from_file' | 'written_to_file' | 'no_action';
    syncedProduct?: ProductMetadata;
    error?: string;
  }> {
    return productMetadata.syncProductMetadataOnRepoChange(productId, product, repoUrl, repoPath);
  }

  //
  // Coding Agents
  //

  async checkCodingAgentInstallation(agent: 'claude-code' | 'cursor' | 'gemini'): Promise<{
    success: boolean;
    installed: boolean;
    agent: string;
    command?: string;
    message?: string;
    error?: string;
  }> {
    return codingAgents.checkCodingAgentInstallation(agent);
  }

  //
  // Code Indexing
  //

  async indexCodebase(path: string, force: boolean = false): Promise<{
    success: boolean;
    message?: string;
    indexed_at?: string;
    error?: string;
  }> {
    return codeIndex.indexCodebase(path, force);
  }

  async getCodeIndexStatus(path: string): Promise<{
    status: 'indexed' | 'not_indexed' | 'indexing' | 'error' | 'unavailable' | 'unknown';
    mcp_connected: boolean;
    progress?: number;
    file_count?: number;
    chunk_count?: number;
    index_status?: 'completed' | 'limit_reached';
    last_indexed?: string;
    last_updated?: string;
    last_attempted_progress?: number;
    message?: string;
    checked_at?: string;
    error?: string;
  }> {
    return codeIndex.getCodeIndexStatus(path);
  }

  async clearCodeIndex(path: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    return codeIndex.clearCodeIndex(path);
  }

  async getMcpStatus(): Promise<{
    connected: boolean;
    tools: string[];
    last_error?: string;
  }> {
    return codeIndex.getMcpStatus();
  }

  //
  // Authentication
  //

  async getCurrentUser(): Promise<{ id: number; organizationId: string; name: string; email: string } | null> {
    return auth.getCurrentUser();
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    return auth.testConnection();
  }

  async checkElectronAuth(): Promise<{ 
    success: boolean; 
    user?: { id: number; organizationId: string; name: string; email: string }; 
    error?: string 
  }> {
    return auth.checkElectronAuth();
  }

  //
  // MCP Operations
  //

  async notifyMcpRootsChanged(product: {
    title: string;
    repositoryUrl: string;
    repoPath?: string | null;
  }): Promise<{ success: boolean; error?: string }> {
    return mcp.notifyMcpRootsChanged(product);
  }

  //
  // JIRA Integration
  //

  async testJiraConnection(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
  }): Promise<{ success: boolean; error?: string }> {
    return jira.testJiraConnection(config);
  }

  async testJiraUserStoryConnection(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
  }): Promise<{ success: boolean; error?: string }> {
    return jira.testJiraUserStoryConnection(config);
  }

  async getJiraProjects(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey?: string;
  }): Promise<{ success: boolean; data?: Array<{ key: string; name: string }>; error?: string }> {
    return jira.getJiraProjects(config);
  }

  async getJiraIssueTypes(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
  }): Promise<{ success: boolean; data?: Array<{ id: string; name: string }>; error?: string }> {
    return jira.getJiraIssueTypes(config);
  }

  async previewJiraIssueImport(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
  }, options?: {
    jql?: string;
    maxResults?: number;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return jira.previewJiraIssueImport(config, options);
  }

  async importJiraIssues(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
  }, options?: {
    jql?: string;
    maxResults?: number;
    updateExisting?: boolean;
    repoUrl?: string;
    repoPath?: string;
  }): Promise<{ 
    success: boolean; 
    data?: {
      total: number;
      successful: number;
      failed: number;
      updated: number;
      created: number;
      skipped: number;
      errors: string[];
    }; 
    error?: string 
  }> {
    return jira.importJiraIssues(config, options);
  }

  async previewJiraUserStoryImport(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
    epicIssueType?: string;
    issueType?: string;
  }, options?: {
    maxResults?: number;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return jira.previewJiraUserStoryImport(config, options);
  }

  async importJiraUserStories(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
    epicIssueType?: string;
    issueType?: string;
  }, options?: {
    maxResults?: number;
    updateExisting?: boolean;
    repoUrl?: string;
    repoPath?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return jira.importJiraUserStories(config, options);
  }

  async exportUserStoriesToJira(config: {
    url: string;
    username: string;
    apiToken: string;
    projectKey: string;
    issueType?: string;
    epicIssueType?: string;
  }, epics: Array<{
    id: string;
    title: string;
    description?: string;
    userStoryIds?: string[];
    section?: string;
    status?: string;
  }>, stories: Array<{
    id: string;
    title: string;
    role?: string;
    goal?: string;
    benefit?: string;
    acceptanceCriteria?: AcceptanceCriterion[];
    section?: string;
    epicId?: string;
    status?: string;
  }>): Promise<{ 
    success: boolean; 
    data?: {
      successful: Array<{ item: any; key: string; type: 'epic' | 'story'; action: 'created' | 'updated' }>;
      failed: Array<{ item: any; error: string; type: 'epic' | 'story' }>;
    }; 
    error?: string 
  }> {
    return jira.exportUserStoriesToJira(config, epics, stories);
  }

  /**
   * Import user stories from CSV data
   */
  async importUserStoriesFromCsv(
    epics: Array<{
      id: string;
      title: string;
      description?: string;
      status?: string;
    }>,
    stories: Array<{
      id: string;
      title: string;
      role?: string;
      goal?: string;
      benefit?: string;
      acceptanceCriteria?: AcceptanceCriterion[];
      status?: string;
      epicId?: string;
    }>,
    options?: {
      updateExisting?: boolean;
      repoUrl?: string;
      repoPath?: string;
    }
  ): Promise<{
    success: boolean;
    data?: {
      successful: number;
      created: number;
      updated: number;
      skipped: number;
      errors: string[];
    };
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.getBaseUrl()}/api/user-stories/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          epics,
          stories,
          updateExisting: options?.updateExisting,
          repoUrl: options?.repoUrl,
          repoPath: options?.repoPath,
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to import user stories',
      };
    }
  }

  //
  // Linear Integration
  //

  async testLinearConnection(config: {
    apiKey: string;
    defaultTeamId?: string;
  }): Promise<{ success: boolean; error?: string }> {
    return linear.testLinearConnection(config);
  }

  async getLinearTeams(config: {
    apiKey: string;
    defaultTeamId?: string;
  }): Promise<{ 
    success: boolean; 
    data?: {
      teams: Array<{ id: string; name: string; key: string }>;
      projects: Array<{ id: string; name: string }>;
    }; 
    error?: string 
  }> {
    return linear.getLinearTeams(config);
  }

  async previewLinearIssueImport(config: {
    apiKey: string;
    defaultTeamId?: string;
  }, options?: {
    teamIds?: string[];
    includeCompleted?: boolean;
    maxResults?: number;
    query?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return linear.previewLinearIssueImport(config, options);
  }

  async importLinearIssues(config: {
    apiKey: string;
    defaultTeamId?: string;
  }, options?: {
    teamIds?: string[];
    includeCompleted?: boolean;
    maxResults?: number;
    query?: string;
    updateExisting?: boolean;
    repoUrl?: string;
    repoPath?: string;
  }): Promise<{ 
    success: boolean; 
    data?: {
      total: number;
      successful: number;
      failed: number;
      updated: number;
      created: number;
      skipped: number;
      results: Array<any>;
      errors: string[];
    }; 
    error?: string 
  }> {
    return linear.importLinearIssues(config, options);
  }

  async previewLinearUserStoryImport(config: {
    apiKey: string;
    defaultTeamId?: string;
  }, options?: {
    teamIds?: string[];
    projectIds?: string[];
    includeCompleted?: boolean;
    maxResults?: number;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return linear.previewLinearUserStoryImport(config, options);
  }

  async importLinearUserStories(config: {
    apiKey: string;
    defaultTeamId?: string;
  }, options?: {
    teamIds?: string[];
    projectIds?: string[];
    includeCompleted?: boolean;
    maxResults?: number;
    updateExisting?: boolean;
    repoUrl?: string;
    repoPath?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    return linear.importLinearUserStories(config, options);
  }

  async exportUserStoriesToLinear(config: {
    apiKey: string;
    defaultTeamId?: string;
  }, epics: Array<{
    id: string;
    title: string;
    description?: string;
    status?: string;
  }>, stories: Array<{
    id: string;
    title: string;
    role?: string;
    goal?: string;
    benefit?: string;
    acceptanceCriteria?: AcceptanceCriterion[];
    epicId?: string;
    status?: string;
  }>, options?: {
    teamId: string;
    createProjects?: boolean;
  }): Promise<{ 
    success: boolean; 
    data?: {
      successful: number;
      created: number;
      updated: number;
      failed: number;
      errors: string[];
    }; 
    error?: string 
  }> {
    return linear.exportUserStoriesToLinear(config, epics, stories, options);
  }

  //
  // Vector Search
  //

  async getVectorSearchStatus(): Promise<{
    connected: boolean;
    serverId: string;
    tools: string[];
    toolCount: number;
    error?: string;
  }> {
    return vectorSearch.getVectorSearchStatus();
  }

  async listVectorSearchRepos(): Promise<{
    repos: Array<{
      repoId: string;
      displayName: string;
      repoPath: string;
      isIndexed: boolean;
      lastIndexed?: string;
      chunkCount: number;
      fileCount: number;
      languages: string[];
    }>;
    totalCount: number;
  }> {
    return vectorSearch.listVectorSearchRepos();
  }

  async registerVectorSearchRepo(repoPath: string, displayName: string, repoId?: string): Promise<{
    success: boolean;
    repoId: string;
    displayName: string;
    collectionName: string;
    message: string;
    error?: string;
  }> {
    return vectorSearch.registerVectorSearchRepo(repoPath, displayName, repoId);
  }

  async getVectorSearchRepoStatus(repoId: string): Promise<{
    repoId: string;
    displayName: string;
    repoPath: string;
    isIndexed: boolean;
    lastIndexed?: string;
    chunkCount: number;
    fileCount: number;
    languages: string[];
  } | null> {
    return vectorSearch.getVectorSearchRepoStatus(repoId);
  }

  async indexVectorSearchRepo(repoId: string, force: boolean = false): Promise<{
    success: boolean;
    repoId: string;
    indexedFiles: number;
    indexedChunks: number;
    duration?: number;
    message: string;
    error?: string;
  }> {
    return vectorSearch.indexVectorSearchRepo(repoId, force);
  }

  async ensureVectorSearchRepoIndexed(repoPath: string, displayName: string, forceReindex: boolean = false): Promise<{
    success: boolean;
    repoId: string | null;
    wasIndexed: boolean;
    message: string;
    indexingStarted?: boolean;
    error?: string;
  }> {
    return vectorSearch.ensureVectorSearchRepoIndexed(repoPath, displayName, forceReindex);
  }

  async notifyVectorSearchProductChanged(product: {
    title: string;
    repositoryUrl: string;
    repoPath?: string | null;
  }): Promise<{ success: boolean; repoId?: string; error?: string }> {
    return vectorSearch.notifyVectorSearchProductChanged(product);
  }

  //
  // Tool Execution
  //

  /**
   * Execute a SkillShop tool
   * 
   * @param tool - The tool to execute (e.g., 'issue-manager')
   * @param operation - The operation to perform (e.g., 'create', 'delete')
   * @param params - Additional parameters for the tool operation
   * @returns The tool execution result
   */
  async executeTool(
    tool: string,
    operation: string,
    params: Record<string, unknown> = {}
  ): Promise<tools.ToolExecutionResult> {
    return tools.executeTool(tool, operation, params);
  }

  /**
   * Create an issue using the issue-manager tool
   */
  async createIssueWithTool(issueData: Record<string, unknown>): Promise<tools.ToolExecutionResult> {
    return tools.createIssue(issueData);
  }

  /**
   * Delete an issue using the issue-manager tool
   */
  async deleteIssueWithTool(issueId: string): Promise<tools.ToolExecutionResult> {
    return tools.deleteIssue(issueId);
  }

  //
  // Changelog
  //

  /**
   * Get the changelog content
   */
  async getChangelog(): Promise<changelog.ChangelogResponse | null> {
    return changelog.getChangelog();
  }
}
